import { createTheme } from '@mui/material/styles';

// assets
import colors from './assets/scss/themes-vars.module.scss';
import "./assets/scss/style.scss"


/**
 * Represent theme style and structure as per Material-UI
 * @param {JsonObject} customization customization parameter object
 */

/**
 * Typography used in theme
 * @param {JsonObject} theme theme customization object
 */
const FONT_FAMILY = "'Poppins', sans-serif";
const FONT_SIZE_BASE = '14px';
const FONT_SIZE_HEADER = '1.125rem';

const componentStyleOverrides=(theme)=> {
    const bgColor = '#fff';
    const fieldBorderRadius = '4px';
    const fieldPaddingY = '6px';
    const fieldPaddingX = '12px';
    const fieldPaddingSmallY = '4px';
    return {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
          InputLabelProps: {
            shrink: true
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& > .MuiInputLabel-root.MuiInputLabel-formControl + .MuiInputBase-root': {
              marginTop: '16px'
            },
            '& > .MuiFormHelperText-root': {
              marginLeft: 0,
              marginTop: '4px'
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'none',
            transformOrigin: 'top left',
            maxWidth: '100%',
            pointerEvents: 'none',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.2,
            color: theme.darkTextPrimary,
            '&.Mui-focused': {
              color: theme.colors?.primaryMain
            },
            '&.Mui-error': {
              color: theme.colors?.errorMain
            },
            '&.Mui-disabled': {
              color: theme.darkTextSecondary
            }
          },
          shrink: {
            transform: 'none'
          },
          outlined: {
            transform: 'none'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: '4px',
            textTransform: 'none',
            height: '32px',
            fontSize: '13px',
            padding: '4px 12px',
            minWidth: '64px',
            '& .MuiButton-startIcon': {
              marginRight: '6px',
            },
            '& .MuiButton-endIcon': {
              marginLeft: '6px',
            },
          },
          sizeSmall: {
            height: '28px',
            padding: '2px 8px',
            fontSize: '12px',
          },
          sizeLarge: {
            height: '38px',
            padding: '6px 16px',
            fontSize: '14px',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '6px',
            borderRadius: '4px',
            '&.MuiIconButton-sizeSmall': {
              padding: '4px',
            },
            '&.MuiIconButton-sizeLarge': {
              padding: '8px',
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '12px',
            padding: '6px 12px',
            verticalAlign: 'middle',
            borderColor: '#f1f5f9',
          },
          head: {
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: '#f8fafc',
            color: '#334155 !important',
            padding: '8px 12px',
            borderBottom: '1px solid #e2e8f0',
            lineHeight: 1.2,
          },
          body: {
            fontSize: '12px',
            fontWeight: 400,
            color: '#1e293b',
          }
        }
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          },
          rounded: {
            borderRadius: `12px`
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            marginBottom: '16px'
          }
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            color: theme.colors?.textDark,
            padding: '10px 14px'
          },
          title: {
            fontSize: FONT_SIZE_HEADER
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px 14px',
            fontSize: FONT_SIZE_BASE
          }
        }
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '10px 14px'
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: theme.darkTextPrimary,
            paddingTop: '10px',
            paddingBottom: '10px',
            '&.Mui-selected': {
              color: theme.menuSelected,
              backgroundColor: theme.menuSelectedBack,
              '&:hover': {
                backgroundColor: theme.menuSelectedBack
              },
              '& .MuiListItemIcon-root': {
                color: theme.menuSelected
              }
            },
            '&:hover': {
              backgroundColor: theme.menuSelectedBack,
              color: theme.menuSelected,
              '& .MuiListItemIcon-root': {
                color: theme.menuSelected
              }
            }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: theme.darkTextPrimary,
            minWidth: '36px'
          }
        }
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: theme.textDark
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            color: theme.textDark,
            '&::placeholder': {
              color: theme.darkTextSecondary,
              fontSize: '0.875rem',
              opacity: 1
            },
            '&::-webkit-input-placeholder': {
              color: theme.darkTextSecondary,
              opacity: 1
            },
            '&::-moz-placeholder': {
              color: theme.darkTextSecondary,
              opacity: 1
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            background: bgColor,
            borderRadius: fieldBorderRadius,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.colors?.grey400
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.colors?.primaryLight
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.colors?.primaryMain,
              borderWidth: 1
            },
            '& legend': {
              display: 'none'
            },
            '&.MuiInputBase-multiline': {
              padding: 0
            }
          },
          input: {
            fontWeight: 400,
            background: bgColor,
            padding: `${fieldPaddingY} ${fieldPaddingX}`,
            borderRadius: fieldBorderRadius,
            '&.MuiInputBase-inputSizeSmall': {
              padding: `${fieldPaddingSmallY} ${fieldPaddingX}`,
              '&.MuiInputBase-inputAdornedStart': {
                paddingLeft: 0
              }
            }
          },
          inputMultiline: {
            padding: `${fieldPaddingY} ${fieldPaddingX}`
          },
          inputAdornedStart: {
            paddingLeft: 4
          },
          notchedOutline: {
            top: 0,
            borderRadius: fieldBorderRadius
          }
        }
      },
      MuiSelect: {
        defaultProps: {
          displayEmpty: true
        },
        styleOverrides: {
          select: {
            padding: `${fieldPaddingY} ${fieldPaddingX} !important`,
            minHeight: '1.25rem',
            '&.mui-select-placeholder': {
              color: theme.darkTextSecondary
            },
            '&.MuiInputBase-inputSizeSmall': {
              padding: `${fieldPaddingSmallY} ${fieldPaddingX} !important`
            }
          }
        }
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              color: theme.colors?.grey300
            }
          },
          mark: {
            backgroundColor: theme.paper,
            width: '4px'
          },
          valueLabel: {
            color: theme?.colors?.primaryLight
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: theme.divider,
            opacity: 1
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: theme.colors?.primaryDark,
            background: theme.colors?.primary200
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '&.MuiChip-deletable .MuiChip-deleteIcon': {
              color: 'inherit'
            }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            color: theme.paper,
            background: theme.colors?.grey700
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '32px'
          },
          indicator: {
            height: '3px',
            borderRadius: '3px',
            backgroundColor: theme.colors?.primaryMain
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '32px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: theme.colors?.grey600,
            padding: '6px 12px',
            '&.Mui-selected': {
              color: theme.colors?.primaryMain
            }
          }
        }
      }
    };
  }

const  themeTypography=(theme)=> {
    return {
      fontFamily: FONT_FAMILY,
      h6: {
        fontWeight: 600,
        color: theme.heading,
        fontSize: FONT_SIZE_HEADER,
        lineHeight: 1.2
      },
      h5: {
        fontSize: FONT_SIZE_HEADER,
        color: theme.heading,
        fontWeight: 600,
        lineHeight: 1.2
      },
      h4: {
        fontSize: FONT_SIZE_HEADER,
        color: theme.heading,
        fontWeight: 600,
        lineHeight: 1.2
      },
      h3: {
        fontSize: FONT_SIZE_HEADER,
        color: theme.heading,
        fontWeight: 600,
        lineHeight: 1.2
      },
      h2: {
        fontSize: FONT_SIZE_HEADER,
        color: theme.heading,
        fontWeight: 600,
        lineHeight: 1.2
      },
      h1: {
        fontSize: FONT_SIZE_HEADER,
        color: theme.heading,
        fontWeight: 600,
        lineHeight: 1.2
      },
      subtitle1: {
        fontSize: FONT_SIZE_BASE,
        fontWeight: 500,
        color: theme.textDark
      },
      subtitle2: {
        fontSize: FONT_SIZE_BASE,
        fontWeight: 400,
        color: theme.darkTextSecondary
      },
      caption: {
        fontSize: FONT_SIZE_BASE,
        color: theme.darkTextSecondary,
        fontWeight: 400
      },
      body1: {
        fontSize: FONT_SIZE_BASE,
        fontWeight: 400,
        lineHeight: '1.334em'
      },
      body2: {
        fontSize: FONT_SIZE_BASE,
        letterSpacing: '0em',
        fontWeight: 400,
        lineHeight: '1.5em',
        color: theme.darkTextPrimary
      },
      button: {
        textTransform: 'capitalize'
      },
      customInput: {
        marginTop: 1,
        marginBottom: 1,
        '& > label': {
          top: 23,
          left: 0,
          color: theme.grey500,
          '&[data-shrink="false"]': {
            top: 5
          }
        },
        '& > div > input': {
          padding: '30.5px 14px 11.5px !important'
        },
        '& legend': {
          display: 'none'
        },
        '& fieldset': {
          top: 0
        }
      },
      mainContent: {
         backgroundColor: theme.background,
        width: '100%',
        minHeight: 'calc(100vh - 100px)',
        flexGrow: 1,
        padding: '10px',
        marginTop: '84px',
        marginRight: '0px',
        marginBottom: '2px',
        borderRadius:'15px',
        boxSizing: 'border-box'
      },
      menuCaption: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.heading,
        padding: '6px',
        textTransform: 'capitalize',
        marginTop: '10px'
      },
      subMenuCaption: {
        fontSize: '0.6875rem',
        fontWeight: 500,
        color: theme.darkTextSecondary,
        textTransform: 'capitalize'
      },
      commonAvatar: {
        cursor: 'pointer',
        borderRadius: '8px'
      },
      smallAvatar: {
        width: '22px',
        height: '22px',
        fontSize: '1rem'
      },
      mediumAvatar: {
        width: '34px',
        height: '34px',
        fontSize: '1.2rem'
      },
      largeAvatar: {
        width: '44px',
        height: '44px',
        fontSize: '1.5rem'
      }
    };
  }
   

const themePalette=(theme)=> {
    return {
      mode: theme?.customization?.navType,
      common: {
        black: theme.colors?.darkPaper
      },
      primary: {
        light: theme.colors?.primaryLight,
        main: theme.colors?.primaryMain,
        dark: theme.colors?.primaryDark,
        200: theme.colors?.primary200,
        800: theme.colors?.primary800
      },
      secondary: {
        light: theme.colors?.secondaryLight,
        main: theme.colors?.secondaryMain,
        dark: theme.colors?.secondaryDark,
        200: theme.colors?.secondary200,
        800: theme.colors?.secondary800
      },
      error: {
        light: theme.colors?.errorLight,
        main: theme.colors?.errorMain,
        dark: theme.colors?.errorDark
      },
      orange: {
        light: theme.colors?.orangeLight,
        main: theme.colors?.orangeMain,
        dark: theme.colors?.orangeDark
      },
      warning: {
        light: theme.colors?.warningLight,
        main: theme.colors?.warningMain,
        dark: theme.colors?.warningDark
      },
      success: {
        light: theme.colors?.successLight,
        200: theme.colors?.success200,
        main: theme.colors?.successMain,
        dark: theme.colors?.successDark
      },
      grey: {
        50: theme.colors?.grey50,
        100: theme.colors?.grey100,
        500: theme.darkTextSecondary,
        600: theme.heading,
        700: theme.darkTextPrimary,
        900: theme.textDark
      },
      dark: {
        light: theme.colors?.darkTextPrimary,
        main: theme.colors?.darkLevel1,
        dark: theme.colors?.darkLevel2,
        800: theme.colors?.darkBackground,
        900: theme.colors?.darkPaper
      },
      text: {
        primary: theme.darkTextPrimary,
        secondary: theme.darkTextSecondary,
        dark: theme.textDark,
        hint: theme.colors?.grey100
      },
      background: {
        paper: theme.paper,
        default: theme.backgroundDefault
      }
    };
  }
  
export const theme = (customization) => {
  const color = colors;

  const themeOption = {
    colors: color,
    heading: color.grey900,
    paper: color.paper,
    backgroundDefault: color.paper,
    background: color.primaryLight,
    darkTextPrimary: color.grey700,
    darkTextSecondary: color.grey500,
    textDark: color.grey900,
    menuSelected: color.secondaryDark,
    menuSelectedBack: 'rgba(33, 150, 243, 0.08)',
    divider: color.grey200,
    customization
  };

  const themeOptions = {
    direction: 'ltr',
    palette: themePalette(themeOption),
    mixins: {
      toolbar: {
        minHeight: '48px',
        padding: '16px',
        '@media (min-width: 600px)': {
          minHeight: '48px'
        }
      }
    },
    typography: themeTypography(themeOption)
  };

  const themes = createTheme(themeOptions);
  themes.components = componentStyleOverrides(themeOption);

  return themes;
};

export default theme;
